import React, { useEffect, useRef } from 'react';
import type { Root } from 'react-dom/client';
import { createRoot } from 'react-dom/client';
import type { IntlShape } from 'react-intl-next';
import { IntlProvider } from 'react-intl-next';

import frameImage from '../assets/PDFexport.png';

import { CoverPageComponent } from './PrintCoverComponent.tsx';
import { isPageViewRoute, preloadImage } from './printHookUtils.ts';

const TARGET_SELECTOR = '#AkMainContent';
const COVER_ID = 'print-cover-page';
const STYLE_DATA_ATTR = 'data-print-cover-style';

function ensurePrintStyles() {
	if (typeof document === 'undefined') return;
	if (document.head.querySelector(`style[${STYLE_DATA_ATTR}="true"]`)) {
		return;
	}

	const style = document.createElement('style');
	style.setAttribute(STYLE_DATA_ATTR, 'true');
	style.textContent = `
				/* Hidden on screen; visible when printing */
				#${COVER_ID} { display: none; }

				@media print {
				#${COVER_ID} {
					display: block !important;
					break-after: page;        /* modern */
					page-break-after: always; /* legacy */
				}
					
				/* Hide only the layout header when printing */
					header[data-layout-slot="true"] {
						display: none !important;
						visibility: hidden !important;
				}
			}`;
	document.head.appendChild(style);
}

export function usePrintCoverInjector(enabled: boolean, intl: IntlShape): void {
	const rootRef = useRef<Root | null>(null);
	const hostRef = useRef<HTMLElement | null>(null);

	useEffect(() => {
		if (!enabled || typeof window === 'undefined' || typeof document === 'undefined') {
			return;
		}

		ensurePrintStyles();

		preloadImage(frameImage);

		const before = () => {
			if (!isPageViewRoute(window.location.pathname)) return;
			// find the main content container where we'll inject the cover page
			const target = document.querySelector<HTMLElement>(TARGET_SELECTOR);

			// exit early if target element doesn't exist
			if (!target) {
				return;
			}

			// check if the cover page host container already exists in the DOM
			let host = document.getElementById(COVER_ID) as HTMLElement | null;
			if (!host) {
				// Create a new host container if it doesn't exist
				host = document.createElement('div');
				host.id = COVER_ID;
			}

			/* insert the host as the first child of target so the cover page prints before content
			only insert if it's not already the first child (prevents duplicate insertions) */
			if (target.firstChild !== host) {
				target.insertBefore(host, target.firstChild);
			}

			/* create a React root for the host container if it doesn't exist yet
			This allows us to render React components into this DOM node */
			if (!rootRef.current) {
				rootRef.current = createRoot(host);
			}

			// render the cover page component with internationalization support
			// wrap it in IntlProvider to provide translations from the parent app
			rootRef.current.render(
				<IntlProvider locale={intl.locale} messages={intl.messages} defaultLocale="en">
					<CoverPageComponent />
				</IntlProvider>,
			);

			// store reference to the host element for cleanup later
			hostRef.current = host;
		};

		const after = () => {
			// clean up the React root to prevent memory leaks
			if (rootRef.current) {
				// unmount the React component tree from the DOM
				rootRef.current.unmount();
				rootRef.current = null;
			}

			// remove the host container from the DOM if it exists and has a parent
			if (hostRef.current?.parentNode) {
				// remove the cover page element from its parent node
				hostRef.current.parentNode.removeChild(hostRef.current);
			}

			// clear the host element reference
			hostRef.current = null;
		};

		window.addEventListener('beforeprint', before);
		window.addEventListener('afterprint', after);

		// safety: some browsers may skip afterprint on cancel
		const onVisibility = () => {
			if (document.visibilityState === 'visible') {
				after();
			}
		};
		document.addEventListener('visibilitychange', onVisibility);

		return () => {
			window.removeEventListener('beforeprint', before);
			window.removeEventListener('afterprint', after);
			document.removeEventListener('visibilitychange', onVisibility);
			after();
		};
	}, [enabled, intl]);
}
